import type { Props } from 'davaux/jsx-runtime'
import type { StyleObject, StyleProps, UiRadius, UiSize } from './types.js'
import { cx, mergeStyle, pickStyleProps, radius } from './types.js'

const ROOT = 'dv-pill'
const LABEL = 'dv-pill-label'
const REMOVE = 'dv-pill-remove'

const REMOVE_ICON =
  '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 15 15" width="0.625em" height="0.625em" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"><path d="M2.5 2.5l10 10M12.5 2.5l-10 10"/></svg>'

/** Visual style variant — default uses the surface background, contrast uses inverted colors. */
export type PillVariant = 'default' | 'contrast'

/** Props for the Pill component. */
export interface PillProps extends StyleProps {
  /** Controls pill height and font size via size tokens. */
  size?: UiSize
  /** Border-radius of the pill shape. */
  radius?: UiRadius
  /** Appends an × button that lets users remove the pill from a list. */
  withRemoveButton?: boolean
  /** Called when the remove button is clicked. */
  onRemove?: (e: MouseEvent) => void
  /** Visual style variant controlling background color treatment. */
  variant?: PillVariant
  /** HTML element or custom component used as the root node. */
  component?: string
  /** Label content displayed inside the pill. */
  children?: Props['children']
  [key: string]: unknown
}

/** Renders a small rounded tag element, optionally with a remove button, suitable for multi-select inputs and tag lists. */
export function Pill({
  size,
  radius: r,
  withRemoveButton,
  onRemove,
  variant = 'default',
  component = 'div',
  class: className,
  style,
  children,
  ...rest
}: PillProps): Promise<string> {
  const cssVars: StyleObject = {}

  if (size !== undefined) {
    cssVars['--dv-pill-fz'] = `var(--dv-pill-fz-${size})`
    cssVars['--dv-pill-height'] = `var(--dv-pill-height-${size})`
  }
  if (r !== undefined) cssVars['--dv-pill-radius'] = radius(r) ?? ''
  const { styleObj, ownRest } = pickStyleProps(rest as Record<string, unknown>)

  const Tag = component as unknown as (p: Props) => Promise<string>
  return (
    <Tag
      class={cx(ROOT, className)}
      style={mergeStyle({ ...cssVars, ...styleObj }, style)}
      data-variant={variant}
      data-with-remove={withRemoveButton || undefined}
      {...ownRest}
    >
      <span class={LABEL}>{children}</span>
      {withRemoveButton && (
        <button
          class={REMOVE}
          type='button'
          aria-label='Remove'
          onMouseDown={(e: MouseEvent) => e.preventDefault()}
          onClick={onRemove}
          dangerouslySetInnerHTML={{ __html: REMOVE_ICON }}
        />
      )}
    </Tag>
  )
}
